// =============================================================================
// useRoleManagementData
//
// Responsibility: subscribe to Redux state + trigger the initial API fetch.
// This is a "data" hook — it owns NO local state and fires NO side effects
// beyond the single useEffect that loads data on mount.
//
// Pattern mirrors useDLOLocationData / useCreateSchedule in the DLO module:
//   - All selector reads are centralised here
//   - The page component gets clean, named values instead of raw useSelector calls
//   - dispatch is exposed so the page can pass it to the handlers hook
// =============================================================================

import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../../store';
import { fetchRoles } from '../../../../../store/thunks/adminThunks';
import {
  selectRoles,
  selectRolesLoading,
  selectRolesSaving,
  selectRolesError,
  selectRolesPagination,
  selectRolesSort,
  selectRolesFilterOptions,
} from '../../../../../store/slices/adminRolesSlice';
import { Role } from '../types';

export interface UseRoleManagementDataReturn {
  roles: Role[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  pagination: {
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };
  sort: {
    sortBy: 'roleName' | 'modifiedAt';
    sortDirection: 'asc' | 'desc';
  };
  filterOptions: {
    availableIn: string[];
    locationTypes: string[];
    statuses: string[];
    organizationTypes: string[];
    threePlStatuses: string[];
    roleTypes: string[];
  };
  dispatch: ReturnType<typeof useAppDispatch>;
}

export const useRoleManagementData = (): UseRoleManagementDataReturn => {
  const dispatch = useAppDispatch();

  // ── Selectors ──────────────────────────────────────────────────────────────
  const roles       = useSelector((state: any) => selectRoles(state));
  const isLoading   = useSelector((state: any) => selectRolesLoading(state));
  const isSaving    = useSelector((state: any) => selectRolesSaving(state));
  const error       = useSelector((state: any) => selectRolesError(state));
  const pagination  = useSelector((state: any) => selectRolesPagination(state));
  const sort        = useSelector((state: any) => selectRolesSort(state));
  const filterOptions = useSelector((state: any) => selectRolesFilterOptions(state));

  // ── Initial fetch ──────────────────────────────────────────────────────────
  // Runs once on mount with default pagination (page 0, sort by name asc).
  useEffect(() => {
    dispatch(fetchRoles({ page: 0, size: 9999, sortBy: 'roleName', sortDirection: 'asc' }));
  }, [dispatch]);

  return { roles, isLoading, isSaving, error, pagination, sort, filterOptions, dispatch };
};
